// src/pages/Auth/LoginPage.jsx
import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { requestRecovery } from "../../services/accountAccess.service";
import { BrandLogo } from "../../components/logo";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import { login } from "../../services/authentication.service";
import "./LoginPage.css";

export default function LoginPage() {
  const toast = useRef(null);
  const location = useLocation();

  // si ProtectedRoute te mandó aquí, viene en state.from
  const from = location.state?.from?.pathname || "/";

  // UI mode: "login" | "recover"
  const [mode, setMode] = useState("login");

  // Login state
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [loadingLogin, setLoadingLogin] = useState(false);

  // Recover state (email only)
  const [recoverEmail, setRecoverEmail] = useState("");
  const [loadingRecover, setLoadingRecover] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");

  const show = (severity, summary, detail) =>
    toast.current?.show({ severity, summary, detail, life: 4000 });

  async function handleLogin(e) {
    e.preventDefault();
    if (!userId || !password) {
      show("warn", "Campos incompletos", "Usuario y contraseña son obligatorios.");
      return;
    }
    try {
      setLoadingLogin(true);
      const res = await login({ username: userId, password });
      const accessToken =
        res?.accessToken || res?.token || res?.jwt || res?.access_token;
      if (!accessToken) {
        show("error", "Error", "El servidor no devolvió un token de acceso.");
        return;
      }

      show("success", "Sesión iniciada", "Autenticación exitosa.");

      // TRUCO: forzamos un reload completo para que AuthProvider se monte
      // con el token ya presente y llame a /users/me
      window.location.replace(from); // en vez de navigate(...)
      // Si prefieres: window.location.href = from;
    } catch (err) {
      const data = err?.response?.data;
      const msg = err?.response?.status === 401 ? "Usuario o contraseña incorrectos."
        : data?.detail || data?.message || err?.message || "No se pudo iniciar sesión.";
      show("error", "Error de autenticación", msg);
    } finally {
      setLoadingLogin(false);
    }
  }

  function handleOpenRecover() {
    setRecoverEmail("");
    setRecoveryMessage("");
    setMode("recover");
  }

  function handleCancelRecover() {
    setMode("login");
  }

  async function handleRecover(e) {
    e.preventDefault();
    if (loadingRecover) return;
    if (!recoverEmail) {
      show("warn", "Campos incompletos", "El correo es obligatorio.");
      return;
    }
    try {
      setLoadingRecover(true);
      const result = await requestRecovery(recoverEmail);
      setRecoveryMessage(result.message);
    } catch (err) {
      const msg =
        err?.response?.data || err?.message || "No se pudo iniciar la recuperación.";
      show("error", "Error", String(msg));
    } finally {
      setLoadingRecover(false);
    }
  }

  return (
    <main className="login-wrapper">
      <Toast ref={toast} className="teamassess-toast" />
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-header">
          <BrandLogo className="login-brand" />
          <h1 id="login-title" className="login-title">{mode === "login" ? "Inicio de sesión" : "Recuperar acceso"}</h1>
        </div>

        {mode === "login" && (
          <form onSubmit={handleLogin} className="p-fluid">
            <div className="field">
              <label htmlFor="userId" className="font-medium">
                Usuario
              </label>
              <InputText
                id="userId"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                autoComplete="username"
              />
            </div>

            <div className="field">
              <label htmlFor="password" className="font-medium">
                Contraseña
              </label>
              <Password
                inputId="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                feedback={false}
                toggleMask
                inputClassName="w-full"
                inputStyle={{ width: "100%" }}
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              label="Entrar"
              icon="pi pi-arrow-right"
              iconPos="right"
              className="mt-2"
              loading={loadingLogin}
            />
            <Button type="button" text label="¿Olvidaste tu contraseña?" onClick={handleOpenRecover} disabled={loadingLogin} />
          </form>
        )}

        {mode === "recover" && (
          <form onSubmit={handleRecover} className="p-fluid">
            <div className="field">
              <label htmlFor="recoverEmail" className="font-medium">
                Correo electrónico
              </label>
              <InputText
                id="recoverEmail"
                value={recoverEmail}
                onChange={(e) => setRecoverEmail(e.target.value)}
                type="email"
                required
                disabled={loadingRecover}
                placeholder="nombre@empresa.com"
                autoComplete="email"
              />
            </div>

            <Button
              type="submit"
              label="Recuperar contraseña"
              className="mt-2"
              loading={loadingRecover}
              disabled={loadingRecover}
            />
            {recoveryMessage && <p role="status" className="mt-4">{recoveryMessage}</p>}

            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Button
                type="button"
                label="Volver al inicio de sesión"
                className="p-button-text"
                onClick={handleCancelRecover}
              />
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
